import React, { useCallback, useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Box from '@mui/material/Box'
import PetSitterCardSkeleton from './PetSitterCardSkeleton'
import NextImage from 'next/image'
import { Routes } from '@/assets/configs'
import useCurrentUser from '@/hooks/useCurrentUser'
import { TokenKeys } from '@/modules/Auth/LocalStorageHelper'
import { appendParamsIfNeeded } from '@/utils'

import { useMediaQuery, useTheme } from '@mui/material'
import {
  EmtAvatar,
  EmtButton,
  EmtTooltip,
  EmtCheckbox,
  EmtChip,
  EmtIcon,
  EmtRating,
  EmtTheme,
  EmtTypography,
  useMobile,
} from 'emt-components-v2'
import { styled } from '@mui/material/styles'
import VariantImages from '@/assets/VariantImages'
import { useTranslation } from 'next-i18next'

import { formatPrice } from '@/utils/formatPrice'

import { PetSitterProfile } from '@/data/PetSitter/PetSitterProfile'
import { InfoTextBox } from '@/components/PetSitter/ReservationCard/InfoTextBox'
import NextALink from '@/components/NextALink'
import LinkButton from '@/components/EMT/LinkButton'
import useFloatingProfile, { ProfileKind } from '@/hooks/useFloatingProfile'
import { useRouter } from 'next/router'
import { usePetsittingMode } from '@/store/usePetsittingMode'
import { openUrlApp } from '@/utils/utils'
import Constants from '@/assets/Constants'
import { gtmSendCustomEvent } from '@/helpers/GTMHelper'
import { getProfileActivityBadge } from '@/helpers/profileActivityBadge'

import { isServiceRejectedReason } from '@/components/pages/pet-sitter/reservations/NewRequest/NewRequest'

import { useNewRequestCartStore } from '@/store/newRequestCartStore'
import { getStatsAsync, type PetsitterStats } from '@/modules/PetSitter/viewProfileServiceHelper'

const SERVICE_NAME_MAP = {
  'garde-chez-eux': 'Garde chez le pet sitter',
  'garde-chez-vous': 'Garde chez le propriétaire',
  promenade: 'Promenade(s)',
  visite: 'Visite(s) à domicile',
} as const

interface PetSitterCardProps extends React.HTMLAttributes<HTMLDivElement> {
  data?: PetSitterProfile
  statsData?: PetsitterStats
  showPrices?: boolean
  select?: boolean
  seeProfile?: boolean
  remove?: boolean
  buttonVariant?: 'text' | 'outlined' | 'contained'
  active?: boolean
  checkbox?: boolean
  alert?: boolean
  small?: boolean
  checked?: boolean
  onCheckboxChange?: (checked: boolean) => void
  handleSendRequest?: () => void
  rejectedReason?: string
  askPetSitter?: boolean
  selectedService?: string
  onMobileNavigate?: ReturnType<typeof useFloatingProfile>['handleOpenProfile']
  selectClickOverride?: () => void
}

export const StyledInlineCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'checked' && prop !== 'rejected' && prop !== 'noService',
})<{ active?: boolean; checked?: boolean; rejected?: boolean; noService?: boolean }>(
  ({ theme, active, checked, rejected, noService }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: checked ? '15px' : '16px',
    gap: '12px',
    flexShrink: 0,
    borderRadius: '18px',
    boxSizing: 'border-box',
    backgroundColor: active ? '#FBF1FE' : 'none',
    border: noService || rejected ? `1px solid #E8E8EA` : checked ? `2px solid transparent` : `1px solid #E8E8EA`,
    borderColor:
      noService || rejected
        ? `#E8E8EA`
        : checked
        ? (theme?.palette as any).purple.dark
        : active
        ? (theme?.palette as any).purple.light
        : '#E8E8EA',
    boxShadow: 'none',
    '& .MuiAvatar-root': {
      width: '100%',
    },
    '&:hover': {
      boxShadow: '0px 0px 5px 1px rgba(83,83,83,0.10)',
      '& a': {
        color: theme?.palette.primary.main,
      },
    },
    '& a': {
      textDecoration: 'none',
    },
  }),
)

export const StyledInlineCardContent = styled(CardContent)(({ theme }: { theme?: EmtTheme }) => ({
  padding: '8px 12px',
  paddingBottom: '12px !important',
  '& a': {
    '&:hover': {
      color: theme?.palette.primary.main,
    },
    color: theme?.palette.greyGradient.main,
    textDecoration: 'none',
  },
  [theme?.breakpoints.down('md')]: {
    padding: '8px 8px',
  },
}))

export const getRejectedInfoText = (reason?: string, name?: string, selectedService?: string) => {
  if (reason === 'date') {
    return (
      <EmtTypography variant="body2">
        <b>{name}</b> n&apos;est pas disponible aux dates sélectionnées, votre demande ne lui sera pas envoyée.
      </EmtTypography>
    )
  }

  if (reason === 'daily-quota-exceeded') {
    return (
      <EmtTypography variant="body2">
        <b>{name}</b> ne sera pas contacté(e) car vous ne pouvez faire que 20 contacts par jour.
      </EmtTypography>
    )
  }

  if (isServiceRejectedReason(reason, selectedService)) {
    return (
      <EmtTypography variant="body2">
        <b>{name}</b> n&apos;effectue pas de{' '}
        <b>{SERVICE_NAME_MAP[selectedService as keyof typeof SERVICE_NAME_MAP] || selectedService}</b>, votre demande ne
        lui sera pas envoyée.
      </EmtTypography>
    )
  }

  return null
}

export const getIndisponibilityInfoText = (reason?: string, name?: string, selectedService?: string) => {
  if (reason === 'date') {
    return (
      <EmtTypography variant="body2">
        <b>{name}</b> n&apos;est pas disponible aux dates sélectionnées.
      </EmtTypography>
    )
  }

  // if (reason === 'daily-quota-exceeded') {
  //   return (
  //     <EmtTypography variant="body2">
  //       <b>{name}</b> ne sera pas contacté(e) car vous ne pouvez faire que 20 contacts par jour.
  //     </EmtTypography>
  //   )
  // }

  if (isServiceRejectedReason(reason, selectedService)) {
    return (
      <EmtTypography variant="body2">
        <b>{name}</b> n&apos;effectue pas de{' '}
        <b>{SERVICE_NAME_MAP[selectedService as keyof typeof SERVICE_NAME_MAP] || selectedService}</b>.
      </EmtTypography>
    )
  }

  return null
}

const PetSitterCard: React.FC<PetSitterCardProps> = ({
  data,
  active,
  checkbox,
  small,
  showPrices = true,
  select,
  seeProfile,
  remove,
  buttonVariant = 'contained',
  checked,
  onCheckboxChange,
  handleSendRequest,
  rejectedReason,
  askPetSitter = true,
  selectedService,
  statsData,
  onMobileNavigate,
  selectClickOverride,
  ...props
}) => {
  const router = useRouter()
  const { currentUser } = useCurrentUser()
  const { isPetsittingMode } = usePetsittingMode()

  const { ...rest } = props

  const {
    uuid,
    name,
    badges,
    title,
    description,
    mainPicture,
    distance,
    reviewsCount,
    rating,
    walkPrice,
    homeVisitPrice,
    sittingAtPetsitterHomePrice,
    sittingAtCustomerHomePrice,
    homeVisitHalfHourPrice,
    walkHalfHourPrice,
    picture,
    city,
    isNew,
    lastRefresh,
  } = data || {}

  const isRejectedByService = isServiceRejectedReason(rejectedReason, selectedService)

  const { data: fetchedStatsData } = useQuery(
    [`petsitter-card-stats-${uuid}`],
    () => getStatsAsync(uuid as string, !currentUser),
    {
      enabled: !!uuid,
      refetchOnWindowFocus: false,
      staleTime: 10000,
    },
  )

  const effectiveStatsData = statsData ?? fetchedStatsData

  const effectiveHasService = !isRejectedByService

  const isRejected = !!rejectedReason

  const rejectedInfoText = getRejectedInfoText(rejectedReason, name, selectedService)

  // Calculate the prices array
  const prices = React.useMemo(() => {
    const getServiceType = (service?: string) => {
      switch (service) {
        case 'walk':
          return 'promenade'
        case 'homeVisit':
          return 'visite'
        case 'sittingAtCustomerHome':
          return 'garde-chez-vous'
        case 'sittingAtPetsitterHome':
          return 'garde-chez-eux'
        default:
          return service
      }
    }

    const allPrices = [
      { type: 'promenade', price: walkPrice, halfPrice: walkHalfHourPrice, icon: 'emt-paws' },
      { type: 'visite', price: homeVisitPrice, halfPrice: homeVisitHalfHourPrice, icon: 'emt-search-property' },
      { type: 'garde-chez-eux', price: sittingAtPetsitterHomePrice, image: VariantImages.SitPrice },
      { type: 'garde-chez-vous', price: sittingAtCustomerHomePrice, image: VariantImages.SitAtHomePrice },
    ].filter((item) => item.price !== undefined && item.price !== null)

    const mappedService = getServiceType(selectedService)

    if (mappedService) {
      const servicePrice = allPrices.filter((item) => item.type === mappedService || item.type === mappedService[0])
      return servicePrice
    }
    return allPrices
  }, [walkPrice, homeVisitPrice, sittingAtPetsitterHomePrice, sittingAtCustomerHomePrice, selectedService])

  const [isMobile] = useMobile()
  const { t } = useTranslation('petsitter')
  const { t: tc } = useTranslation('common')
  const theme = useTheme()
  const isLg = useMediaQuery(theme.breakpoints.down('lg'))
  const petsittersIds = useNewRequestCartStore((s) => s.petsittersIds)
  const addPetsitter = useNewRequestCartStore((s) => s.addPetsitter)
  const removePetsitter = useNewRequestCartStore((s) => s.removePetsitter)
  const addPetsitterDisabled = petsittersIds.length >= 10

  const [croppedDescription, setCroppedDescription] = useState('')
  const normalizedBadges = badges ?? []

  const activityBadge = React.useMemo(
    () =>
      getProfileActivityBadge({
        isNew,
        lastRefresh,
        reviewsCount,
        rating,
        averageResponseTimeUnderThreeHours: effectiveStatsData?.averageResponseTimeUnderThreeHours,
      }),
    [isNew, lastRefresh, reviewsCount, rating, effectiveStatsData?.averageResponseTimeUnderThreeHours],
  )

  const activityBadgeLabel = React.useMemo(
    () =>
      activityBadge
        ? tc(activityBadge.key, {
            defaultValue: activityBadge.defaultValue,
          })
        : undefined,
    [activityBadge, tc],
  )

  useEffect(() => {
    if (!description) return
    const cropText = (text: string, maxLength: number) => {
      return text.length > maxLength ? text.substring(0, maxLength - 3) + '...' : text
    }
    setCroppedDescription(cropText(description, 50)) // Adjust maxLength as needed
  }, [description])

  const handleMobileNavigate = useCallback(() => {
    if (onMobileNavigate) {
      onMobileNavigate(uuid, ProfileKind.PetSitting)
    } else if (uuid) {
      router.push(!!currentUser ? Routes.ProfilePetSitter(uuid) : Routes.PublicProfilePetSitter(uuid))
    }
  }, [onMobileNavigate, uuid])

  if (!data) {
    return <PetSitterCardSkeleton />
  }

  const shouldHideLabel = (iconName: string) => {
    return isMobile && (iconName === 'emt-tick' || iconName === 'emt-id')
  }

  const stop = (e: React.SyntheticEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <NextALink
      aria-label={`Voir le profil de ${name}${city ? `, ${city}` : ''}${
        distance !== undefined && currentUser ? `, à ${Number(distance).toFixed(1)} kilomètres` : ''
      }`}
      style={{
        display: 'block',
        width: '100%',
        textDecoration: 'none',
        color: 'inherit',
      }}
      onClick={(e) => {
        stop(e)
        if (isMobile) {
          handleMobileNavigate?.()
        } else if (uuid) {
          window.open(!!currentUser ? Routes.ProfilePetSitter(uuid) : Routes.PublicProfilePetSitter(uuid), '_blank')
        }
      }}
    >
      <StyledInlineCard
        active={active}
        checked={remove || checked}
        rejected={isRejected}
        noService={!effectiveHasService}
        {...rest}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {!isMobile && !small && (
            <Box
              className="EmtInlineCard-image-wrapper"
              sx={{
                position: 'relative',
                width: isMobile || small ? '75px' : '172px',
                height: isMobile || small ? '75px' : '172px',
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {/* <NextALink href={Routes.ProfilePetSitter(uuid || '')} style={{ width: '100%' }} forceExternal> */}
              <EmtAvatar
                sx={{
                  flexShrink: 0,
                  height: '100%!important',
                  width: '100%',
                }}
                size="large"
                variant="rounded"
                src={picture?.thumbnailUrl || mainPicture?.imageThumbnailUrl || undefined}
                alt={`Photo de profil de ${name}`}
              />
              {activityBadgeLabel && (
                <EmtChip
                  color="transparent"
                  label={activityBadgeLabel}
                  size="small"
                  sx={{
                    position: 'absolute',
                    left: isMobile || small ? '8px' : '13px',
                    top: checkbox ? (isMobile || small ? '36px' : '42px') : isMobile || small ? '8px' : '11.5px',
                    zIndex: 2,
                    fontWeight: 600,
                    '& .MuiChip-label': {
                      padding: '0 8px',
                    },
                  }}
                />
              )}
              {/* </NextALink> */}
              {checkbox && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: isMobile || small ? '8px' : '13px',
                    top: isMobile || small ? '8px' : '11.5px',
                    '& .emt-tick': { backgroundColor: '#AE59C6!important' },
                    '& .emt-icons': {
                      borderColor: 'white!important',
                    },
                  }}
                >
                  <EmtCheckbox
                    variant="purple"
                    sx={{
                      backgroundColor: '#00000033!important',
                    }}
                    onClick={stop}
                    onChange={() => onCheckboxChange && onCheckboxChange(!checked)}
                    checked={checked}
                    controlledCheckbox={true}
                    size="medium"
                  />
                </Box>
              )}
            </Box>
          )}
          <StyledInlineCardContent
            sx={{
              flexGrow: 1,
              padding: '0px !important',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: isMobile ? 'center' : 'flex-start',
                gap: '12px',
                filter: isRejected ? 'grayscale(1)' : 'none',
                opacity: isRejected ? 0.5 : 1,
              }}
            >
              {(isMobile || small) && (
                <Box
                  className="EmtInlineCard-image-wrapper"
                  sx={{
                    position: 'relative',
                    width: small ? '45px' : isMobile ? '75px' : '172px',
                    height: small ? '45px' : isMobile ? '75px' : '172px',
                    overflow: 'hidden',
                    display: 'flex',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {/* <NextALink onClick={handleMobileNavigate} style={{ width: '100%' }}> */}
                  <EmtAvatar
                    sx={{
                      flexShrink: 0,
                      height: '100%!important',
                      width: '100%',
                    }}
                    size="large"
                    variant="rounded"
                    src={picture?.thumbnailUrl || mainPicture?.imageThumbnailUrl || undefined}
                  />
                  {/* {activityBadgeLabel && (
                    <EmtChip
                      color="transparent"
                      label={activityBadgeLabel}
                      size="small"
                      sx={{
                        position: 'absolute',
                        left: isMobile || small ? '8px' : '13px',
                        top: checkbox ? (isMobile || small ? '36px' : '42px') : isMobile || small ? '8px' : '11.5px',
                        zIndex: 2,
                        fontWeight: 600,
                        '& .MuiChip-label': {
                          padding: '0 8px',
                        },
                      }}
                    />
                  )} */}
                  {/* </NextALink> */}
                  {checkbox && (
                    <EmtCheckbox
                      sx={{
                        position: 'absolute',
                        left: isMobile || small ? '8px' : '13px',
                        top: isMobile || small ? '8px' : '11.5px',
                        '& .emt-tick': { backgroundColor: '#AE59C6' },
                        '& .emt-icons': {
                          borderColor: 'white!important',
                        },
                      }}
                      onClick={stop}
                      onChange={() => onCheckboxChange && onCheckboxChange(!checked)}
                      checked={checked}
                      controlledCheckbox={true}
                      size="medium"
                    />
                  )}
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: small ? '4px' : '4px',
                    flexDirection: small || isMobile ? 'column' : 'row',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: '0.3rem' }}>
                    {name && (
                      <EmtTypography variant="h5" fontWeight="bold" lineHeight={1}>
                        <>{name} </>
                      </EmtTypography>
                    )}
                    {city && !currentUser ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '2.5px' }}>
                        <EmtIcon
                          className="emt-pin-1"
                          sx={{ color: 'greyGradient.light', fontWeight: 600 }}
                          fontSize="small"
                        />
                        <EmtTypography variant="caption" color="greyGradient.light" fontWeight={600} lineHeight={0}>
                          {city}
                        </EmtTypography>
                      </Box>
                    ) : (
                      distance !== undefined &&
                      !!currentUser && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2.5px' }}>
                          <EmtIcon
                            className="emt-pin-1"
                            sx={{ color: 'greyGradient.light', fontWeight: 600 }}
                            fontSize="small"
                          />
                          <EmtTypography variant="caption" color="greyGradient.light" fontWeight={600} lineHeight={0}>
                            {Number(distance).toFixed(1)} km
                          </EmtTypography>
                        </Box>
                      )
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: '8px' }}>
                    {(reviewsCount ?? 0) > 0 && rating !== undefined && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '2.5px' }}>
                        <EmtRating
                          defaultValue={rating}
                          label={rating.toString()}
                          labelFontSize="caption"
                          labelFontColor="greyGradient.light"
                          max={1}
                          readOnly
                          size="small"
                          boxSx={{ display: 'flex', alignItems: 'center' }}
                        />
                        <EmtTypography
                          variant="caption"
                          color="greyGradient.light"
                          fontWeight={400}
                          fontSize={10}
                          lineHeight={0}
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          ({t('petSitterCard.review', { count: reviewsCount })})
                        </EmtTypography>
                      </Box>
                    )}
                    {effectiveStatsData?.averageResponseTime && (
                      <Box display="flex" flexDirection="column" gap="0px">
                        <Box display="flex" flexDirection="row" gap="2.5px" alignItems="center">
                          <EmtIcon
                            className="emt-chat-1"
                            sx={{ color: 'greyGradient.light', fontWeight: 600 }}
                            fontSize="small"
                          />
                          <EmtTypography variant="caption" color="greyGradient.light" fontWeight={600} lineHeight={0}>
                            {effectiveStatsData?.averageResponseTime}
                          </EmtTypography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
                {!small && normalizedBadges.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {normalizedBadges.map((badge, index) =>
                      badge.tooltip ? (
                        <EmtTooltip
                          key={index}
                          title={
                            <Box>
                              {badge.tooltip.title && (
                                <EmtTypography variant="body2" fontWeight="700">
                                  {badge.tooltip.title}
                                </EmtTypography>
                              )}
                              {badge.tooltip.description && (
                                <EmtTypography
                                  variant="caption"
                                  sx={{ '& ul': { listStyleType: 'disc', paddingLeft: '20px' } }}
                                >
                                  {badge.tooltip.description}
                                </EmtTypography>
                              )}
                            </Box>
                          }
                        >
                          <EmtChip
                            icon={<EmtIcon className={badge.label.icon} sx={{ fontSize: '14px!important' }} />}
                            color={badge.color}
                            label={shouldHideLabel(badge.label.icon) ? '' : badge.label.label}
                            size="medium"
                            sx={{
                              fontSize: '12px',
                              fontWeight: 600,
                              '& .MuiChip-label': { paddingLeft: shouldHideLabel(badge.label.icon) ? '0px' : '8px' },
                            }}
                          />
                        </EmtTooltip>
                      ) : (
                        <EmtChip
                          key={index}
                          icon={<EmtIcon className={badge.label.icon} sx={{ fontSize: '14px!important' }} />}
                          color={badge.color}
                          label={shouldHideLabel(badge.label.icon) ? '' : badge.label.label}
                          size="medium"
                          sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            '& .MuiChip-label': { paddingLeft: shouldHideLabel(badge.label.icon) ? '0px' : '8px' },
                          }}
                        />
                      ),
                    )}
                  </Box>
                )}
              </Box>
            </Box>
            {rejectedInfoText ? (
              <InfoTextBox infoText={rejectedInfoText} />
            ) : (
              <>
                {small && normalizedBadges.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {normalizedBadges.map((badge, index) => (
                      <EmtChip
                        key={index}
                        icon={<EmtIcon className={badge.label.icon} sx={{ fontSize: '14px!important' }} />}
                        color={badge.color}
                        label={shouldHideLabel(badge.label.icon) ? '' : badge.label.label}
                        size="medium"
                        sx={{
                          fontSize: '12px',
                          fontWeight: 600,
                          '& .MuiChip-label': { paddingLeft: shouldHideLabel(badge.label.icon) ? '0px' : '8px' },
                        }}
                      />
                    ))}
                  </Box>
                )}
                <Box>
                  {title && (
                    <EmtTypography variant="body2" fontWeight="bold" lineHeight="1.03">
                      {title}
                    </EmtTypography>
                  )}
                  {description && (
                    <EmtTypography
                      variant="caption"
                      lineHeight="1.03"
                      textOverflow="ellipsis"
                      overflow="hidden"
                      whiteSpace="nowrap"
                      display="inline-block"
                      maxWidth="100%"
                    >
                      {croppedDescription}
                    </EmtTypography>
                  )}
                </Box>
                {showPrices && selectedService && (
                  <Box
                    sx={{
                      display: prices.length < 3 ? 'grid' : 'flex',
                      gridTemplateColumns: selectedService ? '1fr' : '1fr 1fr 1fr',
                      justifyContent: prices.length < 3 ? 'flex-start' : 'space-between',
                      alignItems: 'center',
                      flexWrap: small ? 'wrap' : isLg ? 'wrap' : 'nowrap',
                      gap: '10px',
                    }}
                  >
                    {prices.map((priceItem) => (
                      <Box key={priceItem.type} sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        {priceItem.icon ? (
                          <EmtIcon
                            className={priceItem.icon}
                            sx={{ fontWeight: 100, fontSize: '15px!important', color: '#AE59C6' }}
                          />
                        ) : (
                          <NextImage height={15} src={priceItem.image} alt="sitter" />
                        )}
                        <Box sx={{ display: 'flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <EmtTypography
                            variant="body2"
                            fontWeight="bold"
                            lineHeight="1"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              // flexDirection: isMobile || small ? 'column' : 'row',
                              '& span': { fontSize: '12px', fontWeight: '400' },
                            }}
                          >
                            A partir de {formatPrice(priceItem.price)}€&nbsp;
                            <span>
                              /{' '}
                              {priceItem.type.includes('sitting') || priceItem.type.includes('garde')
                                ? t('petSitterCard.night')
                                : t('petSitterCard.hour')}
                            </span>
                          </EmtTypography>
                          {priceItem.halfPrice && selectedService && (
                            <EmtTypography
                              variant="body2"
                              fontWeight="bold"
                              lineHeight="1"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                // flexDirection: isMobile || small ? 'column' : 'row',
                                '& span': { fontSize: '12px', fontWeight: '400' },
                              }}
                            >
                              {formatPrice(priceItem.halfPrice)}€&nbsp;
                              <span style={{ flexShrink: '0', textWrap: 'nowrap' }}>
                                / {t('petSitterCard.halfHour')}
                              </span>
                            </EmtTypography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}
            <Box
              sx={{
                width: isMobile || small ? '100%' : 'auto',
                display: 'flex',
                flexDirection: 'row',
                gap: '8px',
              }}
            >
              {seeProfile && uuid && (
                <Box sx={{ width: '100%' }}>
                  <LinkButton
                    fullWidth
                    href={
                      !isMobile
                        ? !!currentUser
                          ? `${Routes.ProfilePetSitter(uuid)}?type=dogowner`
                          : `${Routes.PublicProfilePetSitter(uuid)}?type=dogowner`
                        : undefined
                    }
                    onClick={(e) => {
                      stop(e)
                      // TO DO: remove
                      if (!isPetsittingMode) {
                        e.preventDefault()
                        const url = `https://app.supersitters.app${
                          !!currentUser ? Routes.ProfilePetSitter(uuid) : Routes.PublicProfilePetSitter(uuid)
                        }?token=${encodeURIComponent(
                          localStorage.getItem(TokenKeys.Auth ?? '') || '',
                        )}&refreshToken=${encodeURIComponent(
                          localStorage.getItem(TokenKeys.Refresh ?? '') || '',
                        )}&type=dogowner`
                        openUrlApp(url)
                      } else if (isMobile) {
                        handleMobileNavigate?.()
                      } else if (uuid) {
                        openUrlApp(
                          `${
                            !!currentUser
                              ? appendParamsIfNeeded(Routes.ProfilePetSitter(uuid))
                              : appendParamsIfNeeded(Routes.PublicProfilePetSitter(uuid))
                          }?type=dogowner`,
                        )
                      }
                    }}
                    forceExternal={!isMobile}
                    aStyle={{ flex: 1 }}
                    boxProps={{ sx: { flex: 1 } }}
                    variant="outlined"
                    color="purple"
                    size="small"
                  >
                    {t('petSitterCard.seeProfile')}
                  </LinkButton>
                </Box>
              )}
              {select && (
                <Box sx={{ width: '100%' }}>
                  <EmtButton
                    aria-label={`Sélectionner ${name}`}
                    onClick={(e) => {
                      stop(e)
                      // TO DO: remove
                      if (!isPetsittingMode && uuid) {
                        e.preventDefault()
                        const url = `https://app.supersitters.app${
                          !!currentUser ? Routes.ProfilePetSitter(uuid) : Routes.PublicProfilePetSitter(uuid)
                        }?token=${encodeURIComponent(
                          localStorage.getItem(TokenKeys.Auth ?? '') || '',
                        )}&refreshToken=${encodeURIComponent(
                          localStorage.getItem(TokenKeys.Refresh ?? '') || '',
                        )}&type=dogowner`
                        openUrlApp(url)
                      } else if (selectClickOverride) {
                        selectClickOverride()
                      } else if (uuid) {
                        addPetsitter(uuid)
                        // Send gtm event when a pet sitter is added to the selection
                        gtmSendCustomEvent('member_add_to_selection_sst', {
                          member_role_variable: 'maitre',
                          member_id_variable: currentUser?.id || 'unknown',
                          brand_variable: Constants.storageID,
                          website_variable: Constants.rootUrl,
                        })
                      }
                    }}
                    variant={buttonVariant || active ? 'contained' : 'outlined'}
                    boxProps={{ sx: { flex: 1 } }}
                    fullWidth
                    color="purple"
                    size="small"
                    disabled={addPetsitterDisabled}
                    startIcon={
                      <Box
                        sx={{
                          backgroundColor: '#fff',
                          borderRadius: '50%',
                          width: '14px',
                          height: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <EmtIcon
                          className="emt-add-small"
                          sx={{
                            fontSize: '17px',
                            color: '#AE59C6',
                          }}
                        />
                      </Box>
                    }
                  >
                    {t('petSitterCard.select')}
                  </EmtButton>
                </Box>
              )}
              {remove && (
                <EmtButton
                  fullWidth
                  aria-label={`Enlever de la sélection ${name}`}
                  onClick={(e) => {
                    stop(e)
                    uuid && removePetsitter(uuid)
                  }}
                  color="purple"
                  variant="outlined"
                  size="small"
                  startIcon={
                    <Box
                      sx={{
                        backgroundColor: '#AE59C6',
                        borderRadius: '50%',
                        width: '14px',
                        height: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #fff',
                      }}
                    >
                      <EmtIcon
                        className="emt-minus-small"
                        sx={{
                          fontSize: '17px',
                          color: '#fff',
                        }}
                      />
                    </Box>
                  }
                >
                  {t('petSitterCard.remove')}
                </EmtButton>
              )}
            </Box>
          </StyledInlineCardContent>
        </Box>
        {/* {alert && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <EmtIcon
            className={'emt-alarm'}
            sx={{ fontWeight: 100, fontSize: '15px!important', color: 'primary.main' }}
          />
          <EmtTypography
            variant="caption"
            fontSize="12px"
            fontWeight="600"
            lineHeight="1.03"
            sx={{
              '& span': { color: 'primary.main', fontWeight: '700' },
            }}
          >
            <Trans
              t={t}
              i18nKey={'petSitterCard.dogownerInteressed'}
              components={{
                span: <span />,
              }}
              values={{
                count: dogownerInteressedCount || 0,
                name: name,
              }}
            />
          </EmtTypography>
        </Box>
      )} */}
      </StyledInlineCard>
    </NextALink>
  )
}

export default PetSitterCard
